<template>
  <div class="bill-flow">
    <div class="page-header">
      <div class="page-title">
        <div class="title-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h1>资金流向（日级别）</h1>
        <div class="title-decoration"></div>
      </div>
      <p class="page-subtitle">历史日级别资金流向数据，分析长期资金趋势</p>
    </div>

    <div class="filter-bar">
      <div class="filter-group">
        <label class="filter-label">股票代码</label>
        <el-input
          v-model="stockCode"
          placeholder="输入股票代码，如 002602"
          class="input-code"
          @keyup.enter="handleQuery"
        ></el-input>
      </div>

      <div class="filter-group">
        <label class="filter-label">开始日期</label>
        <el-date-picker
          v-model="startDate"
          type="date"
          placeholder="选择开始日期"
          value-format="yyyy-MM-dd"
          class="date-picker-single"
        ></el-date-picker>
      </div>

      <div class="filter-group">
        <label class="filter-label">结束日期</label>
        <el-date-picker
          v-model="endDate"
          type="date"
          placeholder="选择结束日期"
          value-format="yyyy-MM-dd"
          class="date-picker-single"
        ></el-date-picker>
      </div>

      <div class="button-group">
        <el-button type="primary" @click="handleFetch" :loading="fetching">
          <i class="el-icon-download"></i>
          获取数据
        </el-button>
        <el-button type="success" @click="handleQuery" :loading="loading">
          <i class="el-icon-search"></i>
          查询
        </el-button>
        <el-button @click="resetFilters">
          <i class="el-icon-refresh-left"></i>
          重置
        </el-button>
      </div>
    </div>

    <div v-if="billData.length > 0" class="bill-content">
      <!-- 统计信息 -->
      <div class="stats-cards">
        <div class="stat-card">
          <div class="stat-label">股票名称</div>
          <div class="stat-value">{{ billData[0]?.stock_name || '-' }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">数据天数</div>
          <div class="stat-value">{{ billData.length }} 天</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">主力净流入总计</div>
          <div class="stat-value" :class="totalMainInflow >= 0 ? 'positive' : 'negative'">
            {{ formatMoney(totalMainInflow) }}
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-label">超大单净流入总计</div>
          <div class="stat-value" :class="totalSuperInflow >= 0 ? 'positive' : 'negative'">
            {{ formatMoney(totalSuperInflow) }}
          </div>
        </div>
      </div>

      <!-- 图表 -->
      <div class="chart-container" ref="chartContainer"></div>

      <!-- 数据表格 -->
      <div class="table-section">
        <h3>详细数据</h3>
        <el-table
          :data="paginatedData"
          border
          stripe
          style="width: 100%"
          :height="400"
        >
          <el-table-column
            prop="trade_date"
            label="日期"
            width="180"
          ></el-table-column>
          <el-table-column prop="main_net_inflow" label="主力净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.main_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.main_net_inflow) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="super_net_inflow" label="超大单净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.super_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.super_net_inflow) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="large_net_inflow" label="大单净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.large_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.large_net_inflow) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="medium_net_inflow" label="中单净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.medium_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.medium_net_inflow) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="small_net_inflow" label="小单净流入" width="120" align="right">
            <template slot-scope="scope">
              <span :class="scope.row.small_net_inflow >= 0 ? 'positive' : 'negative'">
                {{ formatMoney(scope.row.small_net_inflow) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column v-if="dataType === 'history'" prop="close_price" label="收盘价" width="100" align="right">
            <template slot-scope="scope">
              {{ scope.row.close_price ? Number(scope.row.close_price).toFixed(2) : '-' }}
            </template>
          </el-table-column>
          <el-table-column v-if="dataType === 'history'" prop="change_percent" label="涨跌幅(%)" width="100" align="right">
            <template slot-scope="scope">
              <span :class="Number(scope.row.change_percent || 0) >= 0 ? 'positive' : 'negative'">
                {{ scope.row.change_percent !== null && scope.row.change_percent !== undefined ? Number(scope.row.change_percent).toFixed(2) : '-' }}%
              </span>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          v-if="billData.length > pageSize"
          @current-change="handlePageChange"
          :current-page="currentPage"
          :page-size="pageSize"
          :total="billData.length"
          layout="total, prev, pager, next, jumper"
          class="pagination"
        ></el-pagination>
      </div>
    </div>

    <el-empty v-else-if="!loading" description="请输入股票代码并获取数据"></el-empty>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

export default {
  name: 'BillFlow',
  data() {
    // 计算2个月前的日期
    const today = new Date()
    const twoMonthsAgo = new Date()
    twoMonthsAgo.setMonth(today.getMonth() - 2)

    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return {
      stockCode: '002602',
      startDate: formatDate(twoMonthsAgo),
      endDate: formatDate(today),
      billData: [],
      loading: false,
      fetching: false,
      currentPage: 1,
      pageSize: 50,
      chart: null
    }
  },
  computed: {
    paginatedData() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.billData.slice(start, end)
    },
    totalMainInflow() {
      return this.billData.reduce((sum, item) => sum + (item.main_net_inflow || 0), 0)
    },
    totalSuperInflow() {
      return this.billData.reduce((sum, item) => sum + (item.super_net_inflow || 0), 0)
    }
  },
  methods: {
    async handleFetch() {
      if (!this.stockCode) {
        this.$message.warning('请输入股票代码')
        return
      }

      this.fetching = true
      try {
        const payload = { stock_code: this.stockCode }

        const response = await axios.post('/stock/bill/history/fetch', payload)

        if (response.data.status === 'success') {
          this.$message.success(response.data.message)
          // 自动查询获取的数据
          this.handleQuery()
        } else {
          this.$message.error(response.data.message || '获取数据失败')
        }
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.fetching = false
      }
    },

    async handleQuery() {
      if (!this.stockCode) {
        this.$message.warning('请输入股票代码')
        return
      }

      this.loading = true
      try {
        const params = { stock_code: this.stockCode }

        // 添加日期范围筛选
        if (this.startDate) {
          params.start_date = this.startDate
        }
        if (this.endDate) {
          params.end_date = this.endDate
        }

        const response = await axios.get('/stock/bill/history/query', { params })

        if (response.data.status === 'success') {
          this.billData = response.data.data || []
          this.currentPage = 1
          if (this.billData.length > 0) {
            this.$nextTick(() => {
              this.renderChart()
            })
          }
        } else {
          this.$message.error(response.data.message || '查询失败')
          this.billData = []
        }
      } catch (error) {
        console.error('查询失败:', error)
        this.$message.error('查询失败: ' + (error.response?.data?.message || error.message))
        this.billData = []
      } finally {
        this.loading = false
      }
    },

    renderChart() {
      if (!this.$refs.chartContainer) return

      if (this.chart) {
        this.chart.dispose()
      }

      this.chart = echarts.init(this.$refs.chartContainer)

      const dates = this.billData.map(item => item.trade_date)

      // 判断是否有占比数据
      const hasRatioData = this.billData[0]?.main_net_inflow_ratio !== null &&
                          this.billData[0]?.main_net_inflow_ratio !== undefined

      // 构建系列数据
      const series = [
        {
          name: '超大单净流入',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: this.billData.map(item => item.super_net_inflow),
          itemStyle: {
            color: params => params.value >= 0 ? '#d32f2f' : '#00897b'
          }
        },
        {
          name: '大单净流入',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: this.billData.map(item => item.large_net_inflow),
          itemStyle: {
            color: params => params.value >= 0 ? '#f57c00' : '#0288d1'
          }
        },
        {
          name: '中单净流入',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: this.billData.map(item => item.medium_net_inflow),
          itemStyle: {
            color: params => params.value >= 0 ? '#fbc02d' : '#7b1fa2'
          }
        },
        {
          name: '小单净流入',
          type: 'bar',
          xAxisIndex: 0,
          yAxisIndex: 0,
          data: this.billData.map(item => item.small_net_inflow),
          itemStyle: {
            color: params => params.value >= 0 ? '#388e3c' : '#5d4037'
          }
        }
      ]

      // 根据是否有占比数据配置图表
      let option
      if (hasRatioData) {
        // 有占比数据时，显示两个grid
        option = {
          title: {
            text: `${this.billData[0]?.stock_name || this.stockCode} 资金流向`,
            left: 'center',
            textStyle: { color: '#e2e8f0' }
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' }
          },
          legend: {
            data: [
              { name: '超大单净流入', textStyle: { color: '#d32f2f' } },
              { name: '大单净流入', textStyle: { color: '#f57c00' } },
              { name: '中单净流入', textStyle: { color: '#fbc02d' } },
              { name: '小单净流入', textStyle: { color: '#388e3c' } },
              { name: '主力净流入占比', textStyle: { color: '#8b5cf6' } },
              { name: '涨跌幅', textStyle: { color: '#f59e0b' } }
            ],
            top: 30
          },
          grid: [
            { left: '8%', right: '8%', top: '12%', height: '38%' },
            { left: '8%', right: '8%', top: '63%', height: '28%' }
          ],
          xAxis: [
            {
              type: 'category',
              data: dates,
              gridIndex: 0,
              axisLabel: {
                rotate: 0,
                color: '#94a3b8'
              }
            },
            {
              type: 'category',
              data: dates,
              gridIndex: 1,
              axisLabel: {
                rotate: 0,
                color: '#94a3b8'
              }
            }
          ],
          yAxis: [
            {
              type: 'value',
              gridIndex: 0,
              name: '净流入额(万)',
              nameTextStyle: { color: '#94a3b8' },
              axisLabel: { color: '#94a3b8' },
              splitLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.1)' } }
            },
            {
              type: 'value',
              gridIndex: 1,
              name: '金额',
              nameTextStyle: { color: '#94a3b8' },
              axisLabel: { color: '#94a3b8' },
              splitLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.1)' } },
              position: 'left'
            },
            {
              type: 'value',
              gridIndex: 1,
              name: '涨跌幅(%)',
              nameTextStyle: { color: '#f59e0b' },
              axisLabel: { color: '#f59e0b' },
              splitLine: { show: false },
              position: 'right'
            }
          ],
          dataZoom: [
            { type: 'inside', xAxisIndex: [0, 1], start: 0, end: 100 },
            { type: 'slider', xAxisIndex: [0, 1], start: 0, end: 100, height: 20, bottom: 10 }
          ],
          series
        }

        // 添加占比数据
        series.push({
          name: '主力净流入占比',
          type: 'line',
          xAxisIndex: 1,
          yAxisIndex: 1,
          data: this.billData.map(item => (item.main_net_inflow_ratio || 0) * 100),
          smooth: true,
          itemStyle: { color: '#8b5cf6' },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(139, 92, 246, 0.3)' },
                { offset: 1, color: 'rgba(139, 92, 246, 0.05)' }
              ]
            }
          }
        })

        // 添加涨跌幅数据
        series.push({
          name: '涨跌幅',
          type: 'line',
          xAxisIndex: 1,
          yAxisIndex: 2,
          data: this.billData.map(item => (item.change_percent || 0)),
          smooth: true,
          itemStyle: { color: '#f59e0b' },
          lineStyle: { color: '#f59e0b', width: 2 }
        })

        option.series = series
      } else {
        // 无占比数据时，只显示一个grid（当日分钟级数据）
        option = {
          title: {
            text: `${this.billData[0]?.stock_name || this.stockCode} 资金流向`,
            left: 'center',
            textStyle: { color: '#e2e8f0' }
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' }
          },
          legend: {
            data: ['超大单净流入', '大单净流入', '中单净流入', '小单净流入'],
            top: 30,
            textStyle: { color: '#cbd5e1' }
          },
          grid: [
            { left: '8%', right: '8%', top: '15%', height: '70%' }
          ],
          xAxis: [
            {
              type: 'category',
              data: dates,
              gridIndex: 0,
              axisLabel: {
                rotate: 45,
                color: '#94a3b8'
              }
            }
          ],
          yAxis: [
            {
              type: 'value',
              gridIndex: 0,
              name: '净流入额(万)',
              nameTextStyle: { color: '#94a3b8' },
              axisLabel: { color: '#94a3b8' },
              splitLine: { lineStyle: { color: 'rgba(99, 102, 241, 0.1)' } }
            }
          ],
          dataZoom: [
            { type: 'inside', xAxisIndex: 0, start: 0, end: 100 },
            { type: 'slider', xAxisIndex: 0, start: 0, end: 100, height: 20, bottom: 10 }
          ],
          series
        }
      }

      this.chart.setOption(option)

      window.addEventListener('resize', this.handleResize)
    },

    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    },

    handlePageChange(page) {
      this.currentPage = page
    },

    resetFilters() {
      // 计算默认日期（2个月前）
      const today = new Date()
      const twoMonthsAgo = new Date()
      twoMonthsAgo.setMonth(today.getMonth() - 2)

      const formatDate = (date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }

      this.stockCode = '002602'
      this.startDate = formatDate(twoMonthsAgo)
      this.endDate = formatDate(today)
      this.billData = []
      this.currentPage = 1
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },

    formatMoney(value) {
      if (value === null || value === undefined) return '-'
      const num = parseFloat(value)
      if (Math.abs(num) >= 10000) {
        return (num / 10000).toFixed(2) + '万'
      }
      return num.toFixed(2)
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
.bill-flow {
  padding: 20px;
  min-height: calc(100vh - 104px);
}

.page-header {
  text-align: center;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
}

.page-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 8px;
}

.title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a5b4fc;
  border: 1px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 0 20px rgba(99, 102, 241, 0.15);
}

.title-icon svg {
  width: 24px;
  height: 24px;
}

.page-title h1 {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #a5b4fc 0%, #c4b5fd 50%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 1px;
}

.title-decoration {
  width: 60px;
  height: 3px;
  background: linear-gradient(90deg, transparent 0%, #6366f1 50%, transparent 100%);
  border-radius: 2px;
}

.page-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.filter-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  position: relative;
  z-index: 1;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 14px;
  color: #94a3b8;
  font-weight: 500;
}

.input-code {
  width: 200px;
}

.input-code :deep(.el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #e2e8f0;
  border-radius: 8px;
}

.date-picker-single {
  width: 180px;
}

.date-picker-single :deep(.el-input__inner) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(99, 102, 241, 0.2);
  color: #e2e8f0;
  border-radius: 8px;
}

.button-group {
  display: flex;
  gap: 10px;
  margin-left: auto;
}

.button-group :deep(.el-button--primary) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}

.button-group :deep(.el-button--success) {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-weight: 500;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);
}

.button-group :deep(.el-button--default) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 24px;
  color: #cbd5e1;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background: rgba(15, 23, 42, 0.6);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  text-align: center;
}

.stat-label {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #e2e8f0;
}

.stat-value.positive {
  color: #ef5350;
}

.stat-value.negative {
  color: #26a69a;
}

.chart-container {
  height: 650px;
  background: rgba(15, 23, 42, 0.6);
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.15);
  padding: 20px;
  margin-bottom: 20px;
}

.table-section {
  background: rgba(15, 23, 42, 0.6);
  border-radius: 12px;
  padding: 20px;
  border: 1px solid rgba(99, 102, 241, 0.15);
}

.table-section h3 {
  margin: 0 0 15px 0;
  font-size: 18px;
  color: #e2e8f0;
}

.table-section :deep(.el-table) {
  background: rgba(15, 23, 42, 0.5);
}

.table-section :deep(.el-table__header-wrapper) {
  background: rgba(30, 41, 59, 0.9);
}

.table-section :deep(.el-table__header th) {
  background: transparent;
  color: #cbd5e1;
  font-weight: 600;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
}

.table-section :deep(.el-table__body tr) {
  background: transparent;
}

.table-section :deep(.el-table__body tbody tr:hover > td) {
  background: rgba(99, 102, 241, 0.08) !important;
}

.table-section :deep(.el-table__body td) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: #e2e8f0;
}

.positive {
  color: #ef5350;
}

.negative {
  color: #26a69a;
}

.pagination {
  margin-top: 20px;
  text-align: center;
}

.pagination :deep(.el-pagination) {
  color: #94a3b8;
}

.pagination :deep(.el-pagination .el-pager li) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  margin: 0 4px;
}

.pagination :deep(.el-pagination .el-pager li.active) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-color: transparent;
  color: #ffffff;
}

.pagination :deep(.el-pagination button) {
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #94a3b8;
}

/* 日期选择器弹出面板样式 */
:deep(.el-date-picker__dropdown.el-popper) {
  background: rgba(15, 23, 42, 0.95) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  backdrop-filter: blur(20px);
}

:deep(.el-picker-panel) {
  background: rgba(15, 23, 42, 0.95) !important;
  border: 1px solid rgba(99, 102, 241, 0.2) !important;
  color: #e2e8f0 !important;
}

:deep(.el-date-picker__header-label) {
  color: #e2e8f0 !important;
}

:deep(.el-picker-panel__icon-btn) {
  color: #94a3b8 !important;
}

:deep(.el-picker-panel__icon-btn:hover) {
  color: #a5b4fc !important;
}

:deep(.el-date-table th) {
  color: #94a3b8 !important;
  border-bottom: 1px solid rgba(99, 102, 241, 0.15) !important;
}

:deep(.el-date-table td) {
  color: #e2e8f0 !important;
}

:deep(.el-date-table td.today span) {
  color: #6366f1 !important;
}

:deep(.el-date-table td.available:hover) {
  background: rgba(99, 102, 241, 0.15) !important;
}

:deep(.el-date-table td.in-range div) {
  background: rgba(99, 102, 241, 0.2) !important;
}

:deep(.el-date-table td.start-date span, .el-date-table td.end-date span) {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
  color: #ffffff !important;
}

:deep(.el-date-table td.current:not(.disabled)) span {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%) !important;
  color: #ffffff !important;
}

:deep(.el-month-table td, .el-year-table td) {
  color: #e2e8f0 !important;
}

:deep(.el-month-table td.today, .el-year-table td.today) {
  color: #6366f1 !important;
}

:deep(.el-month-table td:hover, .el-year-table td:hover) {
  background: rgba(99, 102, 241, 0.15) !important;
}
</style>
